import React, { useState } from 'react';
import { ModalX } from '../../../components';
import { useCreateUGroup, useUpdateUGroup } from '../../../custom-hooks/use-user-groups';
import { Button, Checkbox, Input, Option, Select, Textarea } from '@material-tailwind/react';
import { ProtectedRoutes } from '../../../router-x/routes';
import { STATIC_USER_ROLES, STATIC_USER_ROLES_ENUM } from '../../../utils/enums';
import { convertToPresentableText } from '../../../utils/functions';
import { CornerDownRightIcon } from 'lucide-react';

const UserGroupsFormModal = ({
    open,
    onClose,
    onSubmit,
    isUpdating,
    selectedUserGroupId,
    defaultData
}) => {
    const [formData, setFormData] = useState(defaultData);
    // const [routePermissions, setRoutePermissions] = useState({});

    // Setup initial permissions state based on formData.routes
    // useEffect(() => {
    //     const initialPermissions = {};

    //     // Initialize permissions object based on routes in formData
    //     if (formData.routes && formData.routes.length > 0) {
    //         // For each route in the formData, set it as checked
    //         formData.routes.forEach(routeId => {
    //             initialPermissions[routeId] = { checked: true };
    //         });
    //     }

    //     setRoutePermissions(initialPermissions);
    // }, [defaultData, formData]);

    const addUserGroupMutation = useCreateUGroup();
    const updateUserGroupMutation = useUpdateUGroup();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // const handleSelectChange = (value) => {
    //     setFormData({
    //         ...formData,
    //         recipients: value
    //     });
    // };

    const handleMultiSelectChange = (value) => {
        setFormData({
            ...formData,
            recipients:
                formData.recipients ? (
                    formData.recipients.includes(value) ?
                        formData.recipients.filter(i => i !== value) :
                        [...formData.recipients, value]
                ) : []
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isUpdating) {
                await updateUserGroupMutation.mutateAsync({ userGroupId: selectedUserGroupId, data: formData });
            } else {
                await addUserGroupMutation.mutateAsync(formData);
            }
            onSubmit(isUpdating);
            onClose();
        } catch (error) {
            console.error("Error saving user group:", error);
        }
    };

    const handleRouteCheckboxChange = (e, routeId, route) => {
        const { checked } = e.target;

        // Update the routePermissions state
        // setRoutePermissions(prev => ({
        //     ...prev,
        //     [routeId]: {
        //         ...prev[routeId],
        //         checked
        //     }
        // }));

        // Get new routes list
        let newRoutes = [...formData.routes];

        if (checked) {
            // Add the route if not already in the list
            if (!newRoutes.includes(routeId)) {
                newRoutes.push(routeId);
            }

            // If there's only one permission available, add it too
            if (route && route.availableOperations && route.availableOperations.length === 1) {
                const operationKey = `${routeId}:${route.availableOperations[0]}`;
                if (!newRoutes.includes(operationKey)) {
                    newRoutes.push(operationKey);
                }
            }
        } else {
            // Remove the route
            newRoutes = newRoutes.filter(route => route !== routeId);

            // Remove all related operations
            if (route && route.availableOperations) {
                route.availableOperations.forEach(operation => {
                    const operationKey = `${routeId}:${operation}`;
                    newRoutes = newRoutes.filter(r => r !== operationKey);
                });
            }
        }

        // Update formData
        setFormData(prev => ({
            ...prev,
            routes: newRoutes
        }));
    };

    const handleOperationCheckboxChange = (e, routeId, operation, route) => {
        const { checked } = e.target;
        const operationKey = `${routeId}:${operation}`;

        // Get the current routes list
        let newRoutes = [...formData.routes];

        if (checked) {
            // Add the operation if not already there
            if (!newRoutes.includes(operationKey)) {
                newRoutes.push(operationKey);
            }

            // If there's only one permission and it's being checked, also check the route
            if (route && route.availableOperations && route.availableOperations.length === 1) {
                if (!newRoutes.includes(routeId)) {
                    newRoutes.push(routeId);
                }

                // Update permissions state for the route
                // setRoutePermissions(prev => ({
                //     ...prev,
                //     [routeId]: {
                //         ...prev[routeId],
                //         checked: true
                //     }
                // }));
            }
        } else {
            // Remove the operation
            newRoutes = newRoutes.filter(r => r !== operationKey);

            // If there's only one permission and it's being unchecked, also uncheck the route
            if (route && route.availableOperations && route.availableOperations.length === 1) {
                newRoutes = newRoutes.filter(r => r !== routeId);

                // Update permissions state for the route
                // setRoutePermissions(prev => ({
                //     ...prev,
                //     [routeId]: {
                //         ...prev[routeId],
                //         checked: false
                //     }
                // }));
            }
        }

        // If this is a read permission being turned on/off, update the permissions state
        if (operation === 'R') {
            // setRoutePermissions(prev => ({
            //     ...prev,
            //     [routeId]: {
            //         ...prev[routeId],
            //         hasReadPermission: checked
            //     }
            // }));
        }

        // Update formData
        setFormData(prev => ({
            ...prev,
            routes: newRoutes
        }));
    };

    // Check if a route has a specific operation permission
    const hasOperationPermission = (routeId, operation) => {
        const operationKey = `${routeId}:${operation}`;
        return formData.routes.includes(operationKey);
    };

    // Check if parent route has read permission
    const parentHasReadPermission = (parentRouteId) => {
        return hasOperationPermission(parentRouteId, 'R');
    };

    // Check if a route is selected
    const isRouteSelected = (routeId) => {
        return formData.routes.includes(routeId);
    };

    return (
        <ModalX
            isOpen={open}
            onClose={onClose}
            title={isUpdating ? 'Update User Group' : 'Add User Group'}
            onSubmit={handleSubmit}
            maxWidth='xxxl'
        >
            <form onSubmit={handleSubmit}>
                <div className='flex flex-col md:flex-row gap-4'>
                    <div className='p-4 space-y-4 w-full md:w-1/3'>
                        <Input
                            label='Group Name'
                            name='name'
                            value={formData.name || ''}
                            onChange={handleChange}
                            required
                        />
                        <Textarea
                            label='Group Description/Purpose'
                            name='description'
                            value={formData.description || ''}
                            onChange={handleChange}
                            rows={4}
                        />
                        <Select
                            label='Select Recipients'
                            value={formData.recipients || []}
                            onChange={handleMultiSelectChange}
                            multiple
                        >
                            {STATIC_USER_ROLES_ENUM.filter(role => role !== STATIC_USER_ROLES.USER).map((role, i) => (
                                <Option key={i} value={role}>{convertToPresentableText(role)}</Option>
                            ))}
                        </Select>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {formData.recipients && formData.recipients.map((role, i) => (
                                <div key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                    {convertToPresentableText(role)}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='w-full md:w-2/3 p-4'>
                        <h1 className='text-xl font-bold mb-4'>Allowed Pages & Permissions</h1>
                        <div className='overflow-y-auto max-h-[60vh]'>
                            <div className='grid grid-cols-4 gap-2 mb-2 font-semibold px-2'>
                                <div>Page</div>
                                <div className='col-span-3 grid grid-cols-4'>
                                    <div>Create</div>
                                    <div>Read</div>
                                    <div>Update</div>
                                    <div>Delete</div>
                                </div>
                            </div>
                            <div className='space-y-3 border rounded-lg p-2'>
                                {ProtectedRoutes.filter(route =>
                                    route.inSideBarList &&
                                    !route.permissibleRoles.includes(STATIC_USER_ROLES.USER)
                                ).map((route, i) => (
                                    <div key={i} className='border-b pb-3 last:border-b-0'>
                                        {/* Parent Route */}
                                        <div className='flex items-start gap-2 mb-2'>
                                            <div className='w-1/4'>
                                                <Checkbox
                                                    label={route.name}
                                                    checked={isRouteSelected(route.id)}
                                                    onChange={(e) => handleRouteCheckboxChange(e, route.id, route)}
                                                    className="hover:before:opacity-0"
                                                />
                                            </div>
                                            <div className='w-3/4 grid grid-cols-4 gap-2'>
                                                {route.availableOperations.map((operation, j) => (
                                                    <Checkbox
                                                        key={`${route.id}-op-${j}`}
                                                        label={UserPermissionsRender(operation)}
                                                        checked={hasOperationPermission(route.id, operation)}
                                                        onChange={(e) => handleOperationCheckboxChange(e, route.id, operation, route)}
                                                        disabled={!isRouteSelected(route.id)}
                                                        className="hover:before:opacity-0"
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Nested Routes - only show if parent has Read permission */}
                                        {route?.nestedRoutes?.length > 0 && (
                                            <div className='ml-6 pl-2 border-l-2 border-gray-200'>
                                                {route.nestedRoutes.map((nestedRoute, k) => (
                                                    <div key={`nested-${k}`} className='flex items-start gap-2 mt-2'>
                                                        <div className='w-1/4 flex items-center'>
                                                            <CornerDownRightIcon className='w-4 h-4 text-gray-500 mr-1' />
                                                            <Checkbox
                                                                label={nestedRoute.name}
                                                                checked={isRouteSelected(nestedRoute.id)}
                                                                onChange={(e) => handleRouteCheckboxChange(e, nestedRoute.id, nestedRoute)}
                                                                disabled={!parentHasReadPermission(route.id)}
                                                                className="hover:before:opacity-0"
                                                            />
                                                        </div>
                                                        <div className='w-3/4 grid grid-cols-4 gap-2'>
                                                            {nestedRoute.availableOperations.map((operation, l) => (
                                                                <Checkbox
                                                                    key={`${nestedRoute.id}-op-${l}`}
                                                                    label={UserPermissionsRender(operation)}
                                                                    checked={hasOperationPermission(nestedRoute.id, operation)}
                                                                    onChange={(e) => handleOperationCheckboxChange(e, nestedRoute.id, operation, nestedRoute)}
                                                                    disabled={!isRouteSelected(nestedRoute.id) || !parentHasReadPermission(route.id)}
                                                                    className="hover:before:opacity-0"
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex justify-end gap-2 mt-4 p-4'>
                    <Button
                        variant="outlined"
                        color="gray"
                        className='rounded-full'
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        type='submit'
                        className='rounded-full'
                        loading={isUpdating ? updateUserGroupMutation.isLoading : addUserGroupMutation.isLoading}
                    >
                        {isUpdating ? 'Update' : 'Save'}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default UserGroupsFormModal;

export const UserPermissionsRender = (permission) => {
    switch (permission) {
        case 'C':
            return 'Create';
        case 'R':
            return 'Read Or List';
        case 'U':
            return 'Update';
        case 'D':
            return 'Delete';
        default:
            return convertToPresentableText(permission);
    }
};